import { useState } from 'react';
import { useHayCambios } from '../../hooks/useHayCambios';
import { useCierreConfirmado } from '../../hooks/useCierreConfirmado';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { api, extractErrorMessage } from '../../api/client';
import { useToast } from '../../components/Toast';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Confirm } from '../../components/Confirm';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import type { Categoria, Gasto } from './types';
import { CATEGORIAS, etiquetaCategoria, fechaCorta, inputCls, pesos } from './formato';

interface FormGasto { id?: number; concepto: string; categoria: Categoria; monto: string; diaDelMes: string; activo: boolean; fechaDesde: string; fechaHasta: string; notas: string; }
const gastoVacio: FormGasto = { concepto: '', categoria: 'sueldos', monto: '', diaDelMes: '1', activo: true, fechaDesde: '', fechaHasta: '', notas: '' };

export function GastosTab({ onCambio }: { onCambio: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const { data, loading, refetch } = useApi<Gasto[]>('/api/v3/finanzas/gastos-recurrentes');
  const [form, setForm] = useState<FormGasto | null>(null);
  const [borrar, setBorrar] = useState<Gasto | null>(null);
  const [guardando, setGuardando] = useState(false);
  const cierreForm = useCierreConfirmado(useHayCambios(form, !!form), () => setForm(null));
  const puedeEditar = can('pagos', 'editar');
  const puedeEliminar = can('pagos', 'eliminar');

  const recargar = () => { refetch(); onCambio(); };

  const guardar = async () => {
    if (!form) return;
    const monto = Number(form.monto.replace(',', '.'));
    const dia = Number(form.diaDelMes);
    if (!form.concepto.trim()) { toast.addToast('El concepto es obligatorio', 'error'); return; }
    if (!(monto > 0)) { toast.addToast('El monto debe ser mayor a 0', 'error'); return; }
    if (!Number.isInteger(dia) || dia < 1 || dia > 31) { toast.addToast('El día del mes va de 1 a 31', 'error'); return; }
    setGuardando(true);
    try {
      const body = {
        concepto: form.concepto.trim(), categoria: form.categoria, monto, diaDelMes: dia, activo: form.activo,
        notas: form.notas.trim() || null,
      };
      if (form.id) {
        await api.patch(`/api/v3/finanzas/gastos-recurrentes/${form.id}`, { ...body, fechaDesde: form.fechaDesde || null, fechaHasta: form.fechaHasta || null });
      } else {
        await api.post('/api/v3/finanzas/gastos-recurrentes', {
          ...body, notas: body.notas ?? undefined,
          ...(form.fechaDesde ? { fechaDesde: form.fechaDesde } : {}), ...(form.fechaHasta ? { fechaHasta: form.fechaHasta } : {}),
        });
      }
      toast.addToast(form.id ? 'Gasto actualizado' : 'Gasto creado', 'success');
      setForm(null);
      recargar();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); } finally { setGuardando(false); }
  };

  const eliminar = async () => {
    if (!borrar) return;
    try {
      await api.delete(`/api/v3/finanzas/gastos-recurrentes/${borrar.id}`);
      toast.addToast('Gasto eliminado', 'success');
      recargar();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); } finally { setBorrar(null); }
  };

  const columns = [
    { key: 'concepto', header: 'Concepto', render: (g: Gasto) => <span className="font-medium">{g.concepto}</span> },
    { key: 'categoria', header: 'Categoría', render: (g: Gasto) => etiquetaCategoria(g.categoria) },
    { key: 'monto', header: 'Monto', align: 'right' as const, render: (g: Gasto) => pesos(g.monto) },
    { key: 'diaDelMes', header: 'Día', help: 'Día del mes en que se paga; 31 = último día del mes en meses más cortos', render: (g: Gasto) => g.diaDelMes },
    { key: 'vigencia', header: 'Vigencia', render: (g: Gasto) => g.fechaDesde || g.fechaHasta
      ? `${g.fechaDesde ? fechaCorta(g.fechaDesde) : '…'} – ${g.fechaHasta ? fechaCorta(g.fechaHasta) : '…'}` : 'Siempre' },
    { key: 'activo', header: 'Estado', render: (g: Gasto) => g.activo ? <Badge tone="green">activo</Badge> : <Badge>inactivo</Badge> },
    { key: 'acciones', header: '', render: (g: Gasto) => (
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" disabled={!puedeEditar} title={!puedeEditar ? 'No tenés permiso para realizar esta acción' : undefined}
          onClick={() => setForm({ id: g.id, concepto: g.concepto, categoria: g.categoria, monto: String(g.monto), diaDelMes: String(g.diaDelMes), activo: g.activo, fechaDesde: g.fechaDesde ?? '', fechaHasta: g.fechaHasta ?? '', notas: g.notas ?? '' })}>Editar</Button>
        <Button size="sm" variant="danger" disabled={!puedeEliminar} title={!puedeEliminar ? 'No tenés permiso para realizar esta acción' : undefined}
          onClick={() => setBorrar(g)}>Eliminar</Button>
      </div>
    ) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <p className="text-sm text-slate-500">Gastos fijos de todos los meses (sueldos, alquiler, servicios, impuestos). Se proyectan desde hoy; los de días ya pasados del mes se asumen pagados.</p>
        <Button disabled={!puedeEditar} title={!puedeEditar ? 'No tenés permiso para realizar esta acción' : undefined} onClick={() => setForm({ ...gastoVacio })}>Nuevo gasto</Button>
      </div>
      <DataTable columns={columns} data={data ?? []} loading={loading} title="Gastos-recurrentes" emptyMessage="No hay gastos recurrentes cargados" />

      <Modal open={!!form} cierre={cierreForm} title={form?.id ? 'Editar gasto recurrente' : 'Nuevo gasto recurrente'} size="md">
        {form && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block text-sm text-slate-700 sm:col-span-2">Concepto
                <input value={form.concepto} onChange={e => setForm({ ...form, concepto: e.target.value })} className={inputCls} placeholder="Ej.: Alquiler depósito" />
              </label>
              <label className="block text-sm text-slate-700">Categoría
                <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value as Categoria })} className={inputCls}>
                  {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="block text-sm text-slate-700">Monto mensual ($)
                <input type="number" step="0.01" min="0.01" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} className={inputCls} />
              </label>
              <label className="block text-sm text-slate-700">Día del mes
                <input type="number" min={1} max={31} step={1} value={form.diaDelMes} onChange={e => setForm({ ...form, diaDelMes: e.target.value })} className={inputCls} />
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 pt-6">
                <input type="checkbox" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} className="w-4 h-4" /> Activo
              </label>
              <label className="block text-sm text-slate-700">Desde (opcional)
                <input type="date" value={form.fechaDesde} onChange={e => setForm({ ...form, fechaDesde: e.target.value })} className={inputCls} />
              </label>
              <label className="block text-sm text-slate-700">Hasta (opcional)
                <input type="date" value={form.fechaHasta} onChange={e => setForm({ ...form, fechaHasta: e.target.value })} className={inputCls} />
              </label>
              <label className="block text-sm text-slate-700 sm:col-span-2">Notas
                <input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className={inputCls} />
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={cierreForm.cerrar}>Cancelar</Button>
              <Button onClick={guardar} loading={guardando}>Guardar</Button>
            </div>
          </div>
        )}
      </Modal>

      <Confirm open={!!borrar} title="Eliminar gasto recurrente" danger confirmLabel="Eliminar"
        message={`¿Eliminar "${borrar?.concepto ?? ''}"? Para dejar de proyectarlo sin borrarlo, desactivalo o poné una fecha hasta.`}
        onConfirm={eliminar} onCancel={() => setBorrar(null)} />
    </div>
  );
}
